import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Camera, RotateCw, MousePointerClick, ArrowDown, Keyboard, Globe } from 'lucide-react';
import { runAction, fetchScreenshot } from '@/api/real-browsers';
import type { ActionKind } from '@/types';
import { cn } from '@/lib/cn';

interface Props { instanceId: string; }

/**
 * Right-hand operator panel on the Live page. Each button is an
 * imperative one-shot CDP command — no batching, no scripts. We
 * deliberately keep this small for v1; richer "macro" workflows
 * belong in a follow-up.
 */
export default function ActionPanel({ instanceId }: Props) {
  const [url, setUrl] = useState('');
  const [selector, setSelector] = useState('');
  const [text, setText] = useState('');
  const [key, setKey] = useState('Enter');
  const [scrollY, setScrollY] = useState(400);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const actionM = useMutation({
    mutationFn: ({ kind, detail }: { kind: ActionKind; detail?: Record<string, unknown> }) =>
      runAction(instanceId, kind, detail),
    onSuccess: (r) => {
      setLastResult(r.ok ? `${r.action.kind} ok` : `failed: ${r.error ?? 'unknown'}`);
    },
    onError: (e: any) => setLastResult(`error: ${e?.message ?? 'unknown'}`),
  });

  const screenshotM = useMutation({
    mutationFn: () => fetchScreenshot(instanceId),
    onSuccess: (blob) => {
      // Revoke any previous URL so we don't leak.
      if (screenshot) URL.revokeObjectURL(screenshot);
      setScreenshot(URL.createObjectURL(blob));
      setLastResult('screenshot ok');
    },
    onError: (e: any) => setLastResult(`screenshot failed: ${e?.message}`),
  });

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-4">
      <div>
        <h3 className="text-sm font-medium">Actions</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          One-shot CDP commands against the active page. Results land in the
          Audit page.
        </p>
      </div>

      {/* Quick buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Btn
          icon={Camera} label="Screenshot"
          onClick={() => screenshotM.mutate()}
          busy={screenshotM.isPending}
        />
        <Btn
          icon={RotateCw} label="Reload"
          onClick={() => actionM.mutate({ kind: 'reload_tab' })}
          busy={actionM.isPending}
        />
      </div>

      {/* Navigate */}
      <Field label="Navigate to URL" icon={Globe}>
        <input
          value={url} onChange={e => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs font-mono outline-none focus:border-sky-500"
        />
        <button
          onClick={() => actionM.mutate({ kind: 'navigate', detail: { url } })}
          disabled={!url || actionM.isPending}
          className="px-3 py-1.5 text-xs rounded-md bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/40 hover:bg-sky-500/30 disabled:opacity-40"
        >
          Go
        </button>
      </Field>

      {/* Click selector */}
      <Field label="Click selector" icon={MousePointerClick}>
        <input
          value={selector} onChange={e => setSelector(e.target.value)}
          placeholder="button#submit"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs font-mono outline-none focus:border-sky-500"
        />
        <button
          onClick={() => actionM.mutate({ kind: 'click', detail: { selector } })}
          disabled={!selector || actionM.isPending}
          className="px-3 py-1.5 text-xs rounded-md bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40 hover:bg-emerald-500/30 disabled:opacity-40"
        >
          Click
        </button>
      </Field>

      {/* Type text */}
      <Field label="Type text" icon={Keyboard}>
        <input
          value={text} onChange={e => setText(e.target.value)}
          placeholder="hello world"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs outline-none focus:border-sky-500"
        />
        <button
          onClick={() => actionM.mutate({ kind: 'type', detail: { text } })}
          disabled={!text || actionM.isPending}
          className="px-3 py-1.5 text-xs rounded-md bg-slate-700/40 hover:bg-slate-700/60 disabled:opacity-40"
        >
          Type
        </button>
      </Field>

      {/* Send key */}
      <Field label="Key press">
        <input
          value={key} onChange={e => setKey(e.target.value)}
          placeholder="Enter"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs font-mono outline-none focus:border-sky-500"
        />
        <button
          onClick={() => actionM.mutate({ kind: 'keypress', detail: { key } })}
          disabled={!key || actionM.isPending}
          className="px-3 py-1.5 text-xs rounded-md bg-slate-700/40 hover:bg-slate-700/60 disabled:opacity-40"
        >
          Send
        </button>
      </Field>

      {/* Scroll */}
      <Field label="Scroll by Y px" icon={ArrowDown}>
        <input
          type="number" value={scrollY} onChange={e => setScrollY(Number(e.target.value))}
          className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-xs font-mono outline-none focus:border-sky-500"
        />
        <button
          onClick={() => actionM.mutate({ kind: 'scroll', detail: { dx: 0, dy: scrollY } })}
          disabled={actionM.isPending}
          className="px-3 py-1.5 text-xs rounded-md bg-slate-700/40 hover:bg-slate-700/60 disabled:opacity-40"
        >
          Scroll
        </button>
      </Field>

      {/* Result line + screenshot preview */}
      {lastResult && (
        <div className="text-xs text-slate-400 border-t border-slate-800 pt-3">
          last: <span className="font-mono">{lastResult}</span>
        </div>
      )}
      {screenshot && (
        <div className="border-t border-slate-800 pt-3">
          <img src={screenshot} alt="page screenshot" className="w-full rounded-md ring-1 ring-slate-800" />
        </div>
      )}
    </div>
  );
}

function Btn({ icon: Icon, label, onClick, busy }: {
  icon: typeof Camera; label: string; onClick: () => void; busy?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={cn(
        'flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-md',
        'bg-slate-800/50 ring-1 ring-slate-700 hover:bg-slate-700/60 transition disabled:opacity-50',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function Field({ label, icon: Icon, children }: {
  label: string; icon?: typeof Camera; children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </label>
      <div className="flex gap-2">
        {children}
      </div>
    </div>
  );
}
